// 261010 UI2 — engine / model / độ phân giải (L2): form Tạo plan + ngăn Sửa từng video (THUẦN, không antd).
// 2.0: model mini | fast | pro + độ phân giải; 2.5: CHỈ độ phân giải (BE từ chối `model` ở 2.5). KHÔNG hiện giá.
import type { EventGeneration, GenerationBody, GenerationEngine, GenerationResolution, GenTuning } from '../../types/film';
import { apiErrorText } from './apiErrorText';

export const GENERATION_RESOLUTIONS: ReadonlyArray<{ value: GenerationResolution; label: string }> = [
  { value: '480p', label: '480p' }, { value: '720p', label: '720p' }, { value: '1080p', label: '1080p' },
];
export const GENERATION_MODELS_20: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'mini', label: 'Mini' }, { value: 'fast', label: 'Fast' }, { value: 'pro', label: 'Đầy đủ' },
];
export const GENERATION_ENGINES: ReadonlyArray<{ value: GenerationEngine; label: string }> = [
  { value: 'SEEDANCE_20', label: 'Seedance 2.0' }, { value: 'SEEDANCE_25', label: 'Seedance 2.5' },
];
export interface GenerationForm { engine: GenerationEngine; model?: string | null; resolution?: string | null }
export const DEFAULT_GENERATION_FORM: GenerationForm = { engine: 'SEEDANCE_20' };

const RES = new Set<string>(GENERATION_RESOLUTIONS.map((r) => r.value));
const MODELS = new Set<string>(GENERATION_MODELS_20.map((m) => m.value));

/** Đưa form về trạng thái hợp lệ sau khi đổi engine: 2.5 bỏ model; 2.0 có model (mặc định mini); thiếu độ phân giải ⇒ 480p. */
export function generationFormFixOf(f: GenerationForm): GenerationForm {
  const resolution = f.resolution && RES.has(f.resolution) ? f.resolution : '480p';
  if (f.engine === 'SEEDANCE_25') return { engine: 'SEEDANCE_25', model: null, resolution };
  return { engine: 'SEEDANCE_20', model: f.model && MODELS.has(f.model) ? f.model : 'mini', resolution };
}

export function generationBodyOf(f: GenerationForm | null | undefined): { ok: true; body: GenerationBody } | { ok: false; message: string } {
  if (!f) return { ok: false, message: 'Chưa chọn engine.' };
  if (f.engine !== 'SEEDANCE_20' && f.engine !== 'SEEDANCE_25') return { ok: false, message: 'Engine chỉ nhận Seedance 2.0 hoặc 2.5.' };
  if (!f.resolution || !RES.has(f.resolution)) return { ok: false, message: 'Độ phân giải chỉ nhận 480p, 720p hoặc 1080p.' };
  if (f.engine === 'SEEDANCE_25') return { ok: true, body: { engine: 'SEEDANCE_25', resolution: f.resolution as GenerationResolution } };
  if (!f.model || !MODELS.has(f.model)) return { ok: false, message: 'Seedance 2.0 chỉ nhận model Mini, Fast hoặc Đầy đủ.' };
  return { ok: true, body: { engine: 'SEEDANCE_20', model: f.model, resolution: f.resolution as GenerationResolution } };
}

export function generationLabelOf(g: { engine: GenerationEngine; model?: string | null; resolution?: string | null } | null | undefined): string {
  if (!g) return 'chưa chọn';
  const eng = g.engine === 'SEEDANCE_25' ? '2.5' : '2.0';
  const model = g.engine === 'SEEDANCE_25' ? '' : GENERATION_MODELS_20.find((m) => m.value === g.model)?.label ?? String(g.model ?? '');
  return [eng, model, g.resolution ?? ''].filter(Boolean).join(' · ');
}

export function generationFormOf(g: EventGeneration | null | undefined): GenerationForm {
  return g ? generationFormFixOf({ engine: g.engine, model: g.engine === 'SEEDANCE_25' ? null : g.model, resolution: g.resolution }) : DEFAULT_GENERATION_FORM;
}

export function sameGeneration(a: GenerationForm | null | undefined, b: GenerationForm | null | undefined): boolean {
  if (!a || !b) return false;
  const x = generationFormFixOf(a); const y = generationFormFixOf(b);
  return x.engine === y.engine && x.resolution === y.resolution && (x.engine === 'SEEDANCE_25' || x.model === y.model);
}

const CODE_TEXT: Record<string, string> = {
  INVALID_GENERATION_CONFIG: 'cấu hình engine không hợp lệ — chọn lại engine / model / độ phân giải.',
  ENGINE_PROVIDER_UNSUPPORTED: 'máy chủ đang không gen được Seedance 2.5 — chọn 2.0 hoặc báo kỹ thuật.',
  STRUCTURE_TEMPLATE_NOT_READY: 'mẫu chia video chưa sẵn sàng ở máy chủ — thử lại sau.',
  JOB_RUNNING: 'plan đang có việc chạy ở nền — đợi xong rồi đổi, đừng bấm lại.',
  GENERATION_NOT_CONFIGURED: 'plan này chưa chọn engine lúc tạo — không đổi riêng từng video được.',
  GENERATION_CONFIG_INVALID: 'cấu hình engine của plan bị hỏng — báo kỹ thuật.',
  NEEDS_SPLIT: 'video quá dài cho engine 2.0 — để máy tự chia ở bước Đạo diễn rồi đổi.',
  PROMPT_EDITED_TIMING: 'prompt Kỹ thuật đã sửa tay mang mốc giây cũ — viết lại prompt hoặc xác nhận giữ nguyên.',
  AGENT_RAW_EVENT: 'video thô của agent không đổi engine được.',
  NOT_SKY_PLAN: 'plan không dùng luồng mới.',
};

export function generationErrorText(err: any, what = 'Đổi engine'): string {
  const code = err?.response?.data?.code;
  if (typeof code === 'string' && CODE_TEXT[code]) return `${what}: ${CODE_TEXT[code]}`;
  return apiErrorText(err, what);
}

// ───────────────────────── cấu hình ĐÃ LƯU của video = nguồn duy nhất cho mọi đường gen (review UI2 #3) ─────────────────────────
type SavedGen = { engine?: string; model?: string | null; resolution?: string | null };
type GenHolder = { generation?: SavedGen | null } | null | undefined;
export interface OptInTuning { model?: 'mini' | 'fast' | 'pro'; resolution: GenerationResolution }

/**
 * Video thuộc plan opt-in (`ev.generation` từ GET events) ⇒ model + độ phân giải ĐÃ LƯU (2.5: chỉ độ phân giải — BE khoá model). Hỏng / thiếu / lạ ⇒ null (rơi về luật cũ, không bịa).
 * BE trả 409 GENERATION_CONFIG_MISMATCH nếu lượt gen gửi model / độ phân giải khác cấu hình này.
 */
export function optInTuningOf(ev: GenHolder): OptInTuning | null {
  const g = ev?.generation;
  if (!g || !g.resolution || !RES.has(g.resolution)) return null;
  const resolution = g.resolution as GenerationResolution;
  if (g.engine === 'SEEDANCE_25') return { resolution };
  if (g.engine === 'SEEDANCE_20' && g.model && MODELS.has(g.model)) return { model: g.model as 'mini' | 'fast' | 'pro', resolution };
  return null;
}

/** Gen lẻ ở UI cũ: video opt-in ⇒ cấu hình đã lưu + số bản theo lượt (không để ⚙️ theo lượt ghi đè); video legacy ⇒ nguyên lựa chọn. */
export function legacyGenTuningOf(ev: GenHolder, t: GenTuning): GenTuning {
  const o = optInTuningOf(ev);
  return o ? { ...(o.model ? { model: o.model } : {}), resolution: o.resolution, count: t.count || 1 } : t;
}

/** Gen cả tập: bỏ model / độ phân giải theo lượt của video opt-in (BE dùng cấu hình đã lưu); video legacy giữ. */
export function batchOverridesOf<T>(tuningByEvent: Record<string, T>, evs: Array<{ id: string } & NonNullable<GenHolder>>): Record<string, T> {
  const optIn = new Set(evs.filter((e) => optInTuningOf(e)).map((e) => e.id));
  return Object.fromEntries(Object.entries(tuningByEvent).filter(([id]) => !optIn.has(id)));
}
